import { useSetAtom } from "jotai"
import { useEffect, useState } from "react"
import {
  aFeedbackData,
  aProgress,
  aShowingFeedback,
  FeedbackQuestionState,
  useAtomValue,
} from "~/atoms"
import useIsSmallScreen from "./useIsSmallScreen"

export type QuestionState =
  | "shown"
  | "shrunk"
  | "collapsed"
  | "answered"
  | "hidden"

export type AnswerState = "positive" | "negative" | null

export function useAnswerFeedback({
  assignBucket,
  abGroup,
}: {
  assignBucket: (
    name: string,
    config: {
      successCondition?: string
      meta?: object
    }
  ) => Promise<void>
  abGroup: number | null
}) {
  const showingFeedback = useAtomValue(aShowingFeedback)
  const setFeedbackData = useSetAtom(aFeedbackData)
  const progress = useAtomValue(aProgress)
  const isSmall = useIsSmallScreen()

  const [questionState, setQuestionState] = useState<QuestionState>("shown")
  const [answerState, setAnswerState] = useState<AnswerState>(null)

  const [hovering, setHovering] = useState(false)

  // Reset state when a new question appears
  useEffect(() => {
    if (showingFeedback) {
      assignBucket("show-cancel-feedback", {
        successCondition: abGroup === 1 ? "hidden" : "viewed",
        meta: {
          unique_name: "feedback-question-" + showingFeedback.question.questionId,
          version: "0.0.1",
          abGroup,
          abGroups: 2,
          description: "[hide show] feedback question",
        },
      })
      setQuestionState("shown")
      setAnswerState(null)
    }
  }, [showingFeedback?.question.questionId])

  // Sync aFeedbackData on every state change and on mount
  useEffect(() => {
    if (!showingFeedback) return
    const stateMap: Record<QuestionState, FeedbackQuestionState> = {
      shown: "shown",
      shrunk: "shown",
      collapsed: answerState ? "answered" : "closed",
      answered: "answered",
      hidden: answerState ? "answered" : "closed",
    }
    setFeedbackData({
      feedback_question_state: stateMap[questionState],
      feedback_answer_state: answerState,
      feedback_seconds: Math.floor(progress),
      feedback_version: "0.0.1",
      feedback_question: isSmall
        ? showingFeedback.question.phoneQuestion
        : showingFeedback.question.desktopQuestion,
      feedback_question_id: showingFeedback.question.questionId,
    })
  }, [
    questionState,
    answerState,
    showingFeedback?.question.questionId,
    progress,
  ])

  // Auto-shrink after 5s if not hovering
  useEffect(() => {
    if (hovering || !showingFeedback || questionState !== "shown") return
    const timeout = setTimeout(() => setQuestionState("shrunk"), 5000)
    return () => clearTimeout(timeout)
  }, [hovering, showingFeedback?.question.questionId, questionState])

  useEffect(() => {
    if (questionState !== "answered") return
    const timeout = setTimeout(() => setQuestionState("hidden"), 3000)
    return () => clearTimeout(timeout)
  }, [questionState])

  const handleAnswer = (
    value: "positive" | "negative",
    immediate?: boolean
  ) => {
    if (!showingFeedback) return
    setAnswerState(value)
    setQuestionState(immediate ? "hidden" : "answered")
  }

  const questionLabel = showingFeedback
    ? isSmall
      ? showingFeedback.question.phoneQuestion
      : showingFeedback.question.desktopQuestion
    : null

  return {
    showingFeedback,
    questionState,
    setQuestionState,
    answerState,
    hovering,
    setHovering,
    handleAnswer,
    questionLabel,
  }
}
